import { DEFAULT_LEVEL, LEVELS, Level, shiftDate } from "./equalGroups.ts";

/*
 * What the daily puzzle keeps on this device (ADR 0127 §5), under one key and
 * with the read-validate / try-write habit of bestScore.ts. Nothing is sent
 * anywhere. It holds:
 *   - the grade last chosen;
 *   - today's guesses for each grade, so a reload gives back the board as it
 *     was (older days are dropped on write);
 *   - a streak for each grade: days in a row solved.
 * Practice boards never come here. Entries under the two retired levels of the
 * first version (g35, g68; ADR 0127 §9) fail the checks below, so they read as
 * absent and are gone from the next write.
 */
const STORAGE_KEY = "mathpages.dailyPuzzle";

/** A day's progress is kept this many days, then dropped. */
const KEEP_DAYS = 7;

export interface Streak {
    count: number;
    lastSolvedDate: string;
}

interface Stored {
    level: Level;
    /** "<date>:<level>" to the guesses made, each a list of four tile ids. */
    progress: Record<string, string[][]>;
    streaks: Partial<Record<Level, Streak>>;
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const PROGRESS_KEY = /^\d{4}-\d{2}-\d{2}:g[2-8]$/;
const TILE_ID = /^t\d{1,2}$/;

const isLevel = (value: unknown): value is Level => LEVELS.includes(value as Level);

function emptyStore(): Stored {
    return { level: DEFAULT_LEVEL, progress: {}, streaks: {} };
}

function readStore(): Stored {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return emptyStore();
        }
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
            return emptyStore();
        }
        const store = emptyStore();
        if (isLevel(parsed.level)) {
            store.level = parsed.level;
        }
        if (parsed.progress && typeof parsed.progress === "object") {
            for (const [key, guesses] of Object.entries(parsed.progress)) {
                const valid =
                    PROGRESS_KEY.test(key) &&
                    Array.isArray(guesses) &&
                    guesses.length <= 20 &&
                    guesses.every(
                        (ids) =>
                            Array.isArray(ids) &&
                            ids.length === 4 &&
                            ids.every((id) => typeof id === "string" && TILE_ID.test(id))
                    );
                if (valid) {
                    store.progress[key] = guesses as string[][];
                }
            }
        }
        if (parsed.streaks && typeof parsed.streaks === "object") {
            for (const level of LEVELS) {
                const streak = parsed.streaks[level];
                if (
                    streak &&
                    Number.isInteger(streak.count) &&
                    streak.count > 0 &&
                    typeof streak.lastSolvedDate === "string" &&
                    DATE.test(streak.lastSolvedDate)
                ) {
                    store.streaks[level] = { count: streak.count, lastSolvedDate: streak.lastSolvedDate };
                }
            }
        }
        return store;
    } catch {
        return emptyStore();
    }
}

function writeStore(store: Stored): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch {
        // Full, disabled, or unavailable. A streak is not worth an error.
    }
}

export function readLevel(): Level {
    return readStore().level;
}

export function saveLevel(level: Level): void {
    const store = readStore();
    if (store.level !== level) {
        writeStore({ ...store, level });
    }
}

const progressKey = (date: string, level: Level): string => `${date}:${level}`;

/** The guesses already made on this day's board at this level. */
export function readProgress(date: string, level: Level): string[][] {
    return readStore().progress[progressKey(date, level)] ?? [];
}

/** Keeps this day's guesses and drops any day more than a week old. */
export function saveProgress(date: string, level: Level, guesses: string[][]): void {
    const store = readStore();
    const oldest = shiftDate(date, KEEP_DAYS);
    const progress: Record<string, string[][]> = {};
    for (const [key, saved] of Object.entries(store.progress)) {
        if (key.slice(0, 10) >= oldest) {
            progress[key] = saved;
        }
    }
    progress[progressKey(date, level)] = guesses;
    writeStore({ ...store, progress });
}

/**
 * A solve on `date` extends the streak if the last one was the day before,
 * starts it again at 1 if the gap was longer, and changes nothing if the day is
 * already counted. A loss never comes here, so it never extends a streak.
 */
export function recordSolved(level: Level, date: string): Streak {
    const store = readStore();
    const last = store.streaks[level];
    if (last && last.lastSolvedDate === date) {
        return last;
    }
    const next: Streak = {
        count: last && last.lastSolvedDate === shiftDate(date, 1) ? last.count + 1 : 1,
        lastSolvedDate: date,
    };
    writeStore({ ...store, streaks: { ...store.streaks, [level]: next } });
    return next;
}

/** Days in a row solved as of `today`: a streak last extended before yesterday has ended. */
export function currentStreak(level: Level, today: string): number {
    const streak = readStore().streaks[level];
    if (!streak) {
        return 0;
    }
    return streak.lastSolvedDate === today || streak.lastSolvedDate === shiftDate(today, 1) ? streak.count : 0;
}
